<script lang="ts" setup>
import Header from "@/components/organisms/common/Header.vue";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import { useRoute, useRouter } from "vue-router";
import { computed, onBeforeMount, ref } from "vue";
import { useLoader } from "@/utils/loader";
import { useErrorModal } from "@/utils/modal";
import { salaryService } from "@/app/services/SalaryService";
import {
  BalanceHistory,
  CorporationSalarySummary,
  SalarySummary
} from "@/types/balance/BalanceHistory";
import {
  formatFromISOString,
  formatFromUnixTimestamp,
  parseISO,
  subMonths
} from "@/utils/date";
import SalaryCard from "@/components/organisms/mypage/SalaryCard.vue";
import SalaryDetailList from "@/components/organisms/mypage/SalaryDetailList.vue";
import SalaryDetailModal from "@/components/organisms/mypage/SalaryDetailModal.vue";

const errorModal = useErrorModal();
const route = useRoute();
const router = useRouter();
const loader = useLoader();

const history = ref<BalanceHistory | null>(null);
const rewardHistories = ref<BalanceHistory[]>([]);
const selectedCorporationSalarySummary = ref<CorporationSalarySummary | null>(
  null
);
const isShowDetailModal = ref<boolean>(false);

onBeforeMount(async () => {
  try {
    loader.show();
    const transferRequestId = Number(route.query.transferRequestId as string);
    await Promise.all([
      await fetchBalanceHistory(transferRequestId),
      await fetchBalanceHistoriesForRelatedReward(transferRequestId)
    ]);
  } catch (e) {
    await errorModal.showErrorModal(e as Error);
  } finally {
    loader.hide();
  }
});

const transactionDate = computed(() => {
  if (!history.value) return "";
  return formatFromISOString(history.value.createdAt, "yy/MM/dd");
});

const transactionText = computed(() => {
  if (!history.value) return "";
  switch (history.value.transactionType) {
    case "withdrawal":
      return "早期引出";
    case "salary":
      return `定期給与自動引出（${formatFromUnixTimestamp(
        subMonths(parseISO(history.value.createdAt), 1),
        "MM"
      )}月勤務分）`;
    default:
      return "";
  }
});

const statusLabelVariant = computed(() => {
  if (!history.value?.transferRequest) return "";
  switch (history.value.transferRequest.status) {
    case "requested":
      return "warning";
    case "done":
      return "info";
    case "error":
      return "danger";
    default:
      throw new Error("invalid status");
  }
});

const statusLabel = computed(() => {
  if (!history.value?.transferRequest) return "";
  switch (history.value.transferRequest.status) {
    case "requested":
      return "処理中";
    case "done":
      return "処理済";
    case "error":
      return history.value.transferRequest.errorMessage || "不明なエラー";
    default:
      throw new Error("invalid status");
  }
});

const salarySummary = computed(() => {
  const summary = rewardHistories.value.reduce<SalarySummary>(
    (acc, relatedHistory) => {
      acc.reward += salaryService.getReward(relatedHistory);
      acc.optionValue += relatedHistory.optionValue;
      acc.withholdingTax += relatedHistory.withholdingTax;
      acc.totalIncome += salaryService.getTotalIncome(relatedHistory);
      return acc;
    },
    {
      reward: 0,
      optionValue: 0,
      withholdingTax: 0,
      commission: 0,
      totalIncome: 0
    }
  );
  summary.commission = history.value?.optionValue || 0;
  summary.totalIncome -= summary.commission;
  return summary;
});

const corporationSalarySummaries = computed(() => {
  return rewardHistories.value.reduce<CorporationSalarySummary[]>(
    (acc, relatedHistory) => {
      if (!relatedHistory.order) {
        throw new Error("お仕事に紐づいていない明細があるため表示できません");
      }
      const reward = salaryService.getReward(relatedHistory);
      const totalIncome = salaryService.getTotalIncome(relatedHistory);
      const corporation = acc.find(
        c => c.corporationId === relatedHistory.order!.office.corporation.id
      );
      if (!corporation) {
        acc.push({
          corporationId: relatedHistory.order.office.corporation.id,
          name: relatedHistory.order.office.corporation.name,
          totalIncome,
          officeSalarySummaries: [
            {
              officeId: relatedHistory.order.office.id,
              name: relatedHistory.order.office.name,
              reward,
              optionValue: relatedHistory.optionValue,
              withholdingTax: relatedHistory.withholdingTax,
              totalIncome
            }
          ]
        });
        return acc;
      }
      const office = corporation.officeSalarySummaries.find(
        o => o.officeId === relatedHistory.order!.office.id
      );
      if (!office) {
        corporation.officeSalarySummaries.push({
          officeId: relatedHistory.order.office.id,
          name: relatedHistory.order.office.name,
          reward,
          optionValue: relatedHistory.optionValue,
          withholdingTax: relatedHistory.withholdingTax,
          totalIncome
        });
      } else {
        office.reward += reward;
        office.optionValue += relatedHistory.optionValue;
        office.withholdingTax += relatedHistory.withholdingTax;
        office.totalIncome += totalIncome;
      }
      corporation.totalIncome += totalIncome;
      return acc;
    },
    []
  );
});

function onClickBack() {
  router.back();
}

async function fetchBalanceHistory(transferRequestId: number) {
  const response = await salaryService.fetchBalanceHistories({
    transfer_request_id: transferRequestId
  });
  if (response.results.length === 0) {
    throw new Error("給与内訳が見つかりませんでした");
  }
  history.value = response.results[0];
}

async function fetchBalanceHistoriesForRelatedReward(
  transferRequestId: number
) {
  const response = await salaryService.fetchBalanceHistories({
    transfer_request_related_with_reward_id: transferRequestId,
    ordering: "-created_at"
  });
  rewardHistories.value = response.results;
}

function onClickDetail(corporationSalarySummary: CorporationSalarySummary) {
  selectedCorporationSalarySummary.value = corporationSalarySummary;
  isShowDetailModal.value = true;
}

function onCloseDetailModal() {
  isShowDetailModal.value = false;
  selectedCorporationSalarySummary.value = null;
}
</script>
<template>
  <div>
    <Header>
      <template v-slot:left>
        <FeatherIcon icon="chevron-left" @click="onClickBack" />
      </template>
      給与内訳
    </Header>
    <div class="salary-content">
      <div v-if="history" class="salary">
        <div class="title">
          <p class="text-size-14 text-weight-500">
            {{ transactionDate }}
          </p>
          <p class="text-gray-05 text-size-12" data-testid="transaction-text">
            {{ transactionText }}
          </p>
          <BBadge
            pill
            :variant="statusLabelVariant"
            class="status-badge"
            data-testid="status-badge"
            >{{ statusLabel }}</BBadge
          >
        </div>
        <SalaryCard :salarySummary="salarySummary" />
      </div>
      <div class="detail">
        <p class="text-mild text-size-14 text-weight-500 px-3">
          内訳
        </p>
        <SalaryDetailList
          :corporationSalarySummaries="corporationSalarySummaries"
          @clickDetail="onClickDetail"
          class="mt-2"
        />
      </div>
    </div>
    <SalaryDetailModal
      v-if="selectedCorporationSalarySummary"
      :isShowDetailModal="isShowDetailModal"
      :corporationSalarySummary="selectedCorporationSalarySummary"
      @close="onCloseDetailModal"
    />
  </div>
</template>
<style lang="scss" scoped>
.salary-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.salary {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
}

.title {
  display: flex;
  gap: 8px;
}

.status-badge {
  height: 18px;
  font-size: 12px;
  font-weight: 400;
}
</style>
